import { useEffect, useRef, useState } from 'react';
import { formatPrice } from '../../lib/format';

interface PriceRangeFilterProps {
  bounds: { min: number; max: number } | null;
  /** Current applied values (undefined = no bound). */
  value: { min?: number | undefined; max?: number | undefined };
  currency?: string | undefined;
  onChange: (next: { min?: number | undefined; max?: number | undefined }) => void;
}

const DEBOUNCE_MS = 300;

/**
 * Dual price filter built from two native range inputs (zero-dep, keyboard-operable).
 * Emits debounced changes; clamps so min ≤ max.
 */
export function PriceRangeFilter({ bounds, value, currency = 'USD', onChange }: PriceRangeFilterProps) {
  const lo = bounds?.min ?? 0;
  const hi = bounds?.max ?? 0;
  const [minV, setMinV] = useState(value.min ?? lo);
  const [maxV, setMaxV] = useState(value.max ?? hi);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Re-sync when applied values or bounds change externally (URL/back-forward).
  useEffect(() => {
    setMinV(value.min ?? lo);
    setMaxV(value.max ?? hi);
  }, [value.min, value.max, lo, hi]);

  function emit(min: number, max: number) {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      onChange({
        min: min > lo ? min : undefined,
        max: max < hi ? max : undefined,
      });
    }, DEBOUNCE_MS);
  }

  function onMin(v: number) {
    const next = Math.min(v, maxV);
    setMinV(next);
    emit(next, maxV);
  }
  function onMax(v: number) {
    const next = Math.max(v, minV);
    setMaxV(next);
    emit(minV, next);
  }

  const disabled = !bounds || lo === hi;

  return (
    <fieldset className="border-0 p-0">
      <legend className="font-serif text-lg font-bold text-ink">Price per night</legend>
      <p className="mt-1 text-sm font-medium text-clay">
        {formatPrice(minV, currency)} – {formatPrice(maxV, currency)}
      </p>
      <div className="mt-3 space-y-3">
        <input
          type="range" min={lo} max={hi} value={minV} disabled={disabled}
          onChange={(e) => onMin(Number(e.target.value))}
          aria-label="Minimum price"
          aria-valuetext={formatPrice(minV, currency)}
          className="w-full accent-clay"
        />
        <input
          type="range" min={lo} max={hi} value={maxV} disabled={disabled}
          onChange={(e) => onMax(Number(e.target.value))}
          aria-label="Maximum price"
          aria-valuetext={formatPrice(maxV, currency)}
          className="w-full accent-clay"
        />
      </div>
    </fieldset>
  );
}
